/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { SetStateAction } from "react";
import { When } from "react-if";
import { Table, Text, theme, Row, Checkbox } from "@nordcloud/gnui";
import { useToggle } from "~/hooks";
import { inflect, isNotEmpty, isNotNil } from "~/tools";
import { StyledTd, StyledTr, SVGIconArrow, TdWithMinWidth } from "./styles";
import { SubnetOption } from "./types";

type VpcSubnetRowProps = {
  name: string;
  subnets: SubnetOption[];
  selectedSubnets: SubnetOption[];
  setSelectedSubnets: (value: SetStateAction<SubnetOption[]>) => void;
  disabled?: boolean;
  onSubmit: (value: string) => void;
};

export function VpcSubnetRow({
  name,
  subnets,
  selectedSubnets,
  setSelectedSubnets,
}: VpcSubnetRowProps) {
  const [isRowOpen, setRowOpen] = useToggle(
    isVpcExpanded({ selectedSubnets, subnets })
  );

  const subnetNids = subnets.flatMap(({ nid }) => nid);
  const selectedSubnetNids = selectedSubnets
    .flatMap(({ nid }) => nid)
    .filter((selectedSubnet) => subnetNids.includes(selectedSubnet));

  const isVpcSelected = subnetNids.every((nid) =>
    selectedSubnetNids.includes(nid)
  );

  const switchVpcs = () => {
    if (isVpcSelected) {
      const newSelectedSubnets = selectedSubnets.filter(
        (selectedSubnet) =>
          !subnets.find((vpcSubnet) => selectedSubnet.nid === vpcSubnet.nid)
      );
      setSelectedSubnets(newSelectedSubnets);
    } else {
      setSelectedSubnets((prevSelectedSubnets) => [
        ...prevSelectedSubnets,
        ...subnets,
      ]);
    }
  };

  const switchSubnet = (inputSubnet: SubnetOption) => {
    if (selectedSubnetNids.includes(inputSubnet.nid)) {
      const newSelectedSubnets = selectedSubnets.filter(
        ({ subnetId }) => subnetId !== inputSubnet.subnetId
      );
      setSelectedSubnets(newSelectedSubnets);
    } else {
      setSelectedSubnets((prevSelectedSubnets) => [
        ...prevSelectedSubnets,
        inputSubnet,
      ]);
    }
  };

  const hasSubnets = isNotEmpty(subnets ?? []);

  return (
    <Table.tbody>
      <Table.tr>
        <TdWithMinWidth data-testid="vpc-expand" onClick={setRowOpen}>
          <When condition={hasSubnets}>
            <div css={{ transform: "translateY(5px)" }}>
              <SVGIconArrow isRowOpen={isRowOpen} name="chevronDown" />
            </div>
          </When>
        </TdWithMinWidth>
        <TdWithMinWidth>
          <When condition={hasSubnets}>
            <Checkbox
              withoutLabel
              data-testid="vpc-select"
              checked={isVpcSelected}
              onChange={switchVpcs}
            />
          </When>
        </TdWithMinWidth>
        <Table.td css={{ wordBreak: "break-all" }}>
          <Text weight="medium" tag="span">
            {name}
          </Text>
          <Row css={{ marginLeft: 0 }}>
            <Text tag="span" size="sm" color={theme.color.text.text02}>
              {subnets.length} {inflect("Subnet")(subnets.length)}
            </Text>
          </Row>
        </Table.td>
        <StyledTd />
      </Table.tr>
      {subnets.map((subnet) => (
        <StyledTr key={subnet?.nid} show={isRowOpen}>
          <When condition={hasSubnets}>
            <StyledTd colSpan={1} />
            <StyledTd colSpan={1}>
              <Checkbox
                data-testid="subnet-select"
                checked={
                  isVpcSelected || isSubnetSelected(subnet, selectedSubnets)
                }
                onChange={() => {
                  switchSubnet(subnet);
                }}
              />
            </StyledTd>
            <StyledTd colSpan={2}>
              <Text tag="span" size="md">
                {subnet?.subnetId}
              </Text>
            </StyledTd>
          </When>
        </StyledTr>
      ))}
    </Table.tbody>
  );
}

const isVpcExpanded = ({
  selectedSubnets,
  subnets,
}: {
  selectedSubnets: SubnetOption[];
  subnets: SubnetOption[];
}) => {
  const selectedSubnetsCount = subnets.filter((subnet) =>
    isSubnetSelected(subnet, selectedSubnets)
  ).length;

  return selectedSubnetsCount > 0;
};

const isSubnetSelected = (
  subnet: SubnetOption,
  selectedSubnets: SubnetOption[]
) => {
  return isNotNil(
    selectedSubnets.find(({ subnetId }) => subnetId === subnet?.subnetId)
  );
};
